import { useState, useEffect, useRef, useCallback } from 'react';
import { logInfoCtx } from 'app/logging';
import { v4 as uuidv4 } from 'uuid';
import { SalesOrder } from 'services/salesOrders/types';
import { createDefaultSalesOrderPlaceholder } from '../../../../../../services/salesOrders/helpers';
import { showNotification } from '../../../../../../services/api/notifications';
import {
  BulkQuickFulfillStage,
  BulkQuickFulfillProgressDetail,
} from '../types';
import { getErrorMessage } from '../../../../../../helpers/common';
import { useSelector } from 'react-redux';
import { getActiveUser } from 'services/user';

import { StockStatus } from 'ui/theme/components/FBOStockStatusTag/types';

const BQF_INELIGIBILITY_CODES = {
  INVALID_STATUS: 'INVALID_STATUS',
  NOT_EXISTING: 'NOT_EXISTING',
  NO_SALES_ITEMS: 'NO_SALES_ITEMS',
  NO_NON_DELETED_SALES_ITEMS: 'NO_NON_DELETED_SALES_ITEMS',
  ALL_CREDIT_RETURN_OR_DROPSHIP: 'ALL_CREDIT_RETURN_OR_DROPSHIP',
  UNFULFILLABLE_TRACKED_ITEMS: 'UNFULFILLABLE_TRACKED_ITEMS',
};

interface UseBulkQuickFulfillProps {
  selectedOrderIds: number[];
  fetchSalesOrderById: (id: number) => Promise<SalesOrder>;
  quickFulfillSalesOrder: (
    orderId: number,
    options?: { suppressErrorToast?: boolean }
  ) => Promise<any>;
  stockStatusMap: Map<number, StockStatus | null>;
  determineEligibility: (order: SalesOrder) => {
    eligible: boolean;
    reason: string;
    code: string;
  };
  setSelectedItems: (ids: number[]) => void;
  fetchSearchResult: () => void;
}

interface UseBulkQuickFulfillReturn {
  isBulkQuickFulfillModalOpen: boolean;
  bulkQuickFulfillStage: BulkQuickFulfillStage;
  bulkQuickFulfillEligibleOrders: SalesOrder[];
  bulkQuickFulfillIneligibleOrders: {
    order: SalesOrder;
    reason: string;
    code: string;
  }[];
  bulkQuickFulfillProgressDetails: BulkQuickFulfillProgressDetail[];
  isFetchingFullOrders: boolean;
  isStoppingBulkQuickFulfill: boolean;
  openModalAndPrepareOrders: () => Promise<void>;
  closeBulkQuickFulfillModal: () => void;
  confirmAndProcessBulkQuickFulfill: () => Promise<void>;
  stopBulkQuickFulfillProcessing: () => void;
  estimatedTimePerOrderSeconds: number;
}

export const useBulkQuickFulfill = ({
  selectedOrderIds,
  fetchSalesOrderById,
  quickFulfillSalesOrder,
  stockStatusMap,
  determineEligibility,
  setSelectedItems,
  fetchSearchResult,
}: UseBulkQuickFulfillProps): UseBulkQuickFulfillReturn => {
  const ESTIMATED_TIME_PER_ORDER_SECONDS = 5;
  const [isBulkQuickFulfillModalOpen, setIsBulkQuickFulfillModalOpen] =
    useState(false);
  const [bulkQuickFulfillStage, setBulkQuickFulfillStage] =
    useState<BulkQuickFulfillStage>('confirm');
  const [bulkQuickFulfillEligibleOrders, setBulkQuickFulfillEligibleOrders] =
    useState<SalesOrder[]>([]);
  const [
    bulkQuickFulfillIneligibleOrders,
    setBulkQuickFulfillIneligibleOrders,
  ] = useState<{ order: SalesOrder; reason: string; code: string }[]>([]);
  const [bulkQuickFulfillProgressDetails, setBulkQuickFulfillProgressDetails] =
    useState<BulkQuickFulfillProgressDetail[]>([]);
  const [isFetchingFullOrders, setIsFetchingFullOrders] = useState(false);
  const [isStoppingBulkQuickFulfill, setIsStoppingBulkQuickFulfill] =
    useState(false);
  const isStoppingRef = useRef(false);
  const [hasRefreshedAfterBulkFulfill, setHasRefreshedAfterBulkFulfill] =
    useState(false);
  const currentBulkOperationId = useRef<string | undefined>(undefined);

  const activeUser = useSelector(getActiveUser);

  const userContext = {
    userId: activeUser?.user?.id ?? undefined,
  };

  const openModalAndPrepareOrders = useCallback(async () => {
    const preparationStartTime = Date.now();
    currentBulkOperationId.current = uuidv4(); // Generate a new ID for this operation attempt
    logInfoCtx('[BQF_Perf_Monitoring] openModalAndPrepareOrders called', {
      ...userContext,
      component: 'useBulkQuickFulfill',
      title: 'Bulk Quick Fulfill Initiated',
      description: `Preparing to bulk quick fulfill ${selectedOrderIds.length} orders.`,
      selectedOrderCount: selectedOrderIds.length,
      selectedOrderIds: selectedOrderIds.map((o) => o),
      bulkOperationId: currentBulkOperationId.current,
    });
    if (selectedOrderIds.length === 0) return;

    setIsFetchingFullOrders(true);
    setBulkQuickFulfillEligibleOrders([]);
    setBulkQuickFulfillIneligibleOrders([]);
    setIsBulkQuickFulfillModalOpen(false); // Ensure it's closed before fetching, then opened
    setIsStoppingBulkQuickFulfill(false);
    isStoppingRef.current = false;
    setBulkQuickFulfillStage('confirm'); // Reset stage
    setBulkQuickFulfillProgressDetails([]); // Reset progress

    try {
      const results = await Promise.allSettled(
        selectedOrderIds.map((id) => fetchSalesOrderById(id))
      );

      const eligible: SalesOrder[] = [];
      const ineligible: { order: SalesOrder; reason: string; code: string }[] =
        [];

      results.forEach((result, index) => {
        const originalId = selectedOrderIds[index];

        if (result.status === 'fulfilled' && result.value) {
          const order = result.value;
          // Override stockStatus with value from the map derived from list view
          if (order.id) {
            // Ensure order.id exists
            const statusFromMap = stockStatusMap.get(order.id);
            order.stockStatus =
              statusFromMap !== undefined ? statusFromMap : null;
          } else {
            // Should not happen if order.id is guaranteed by fetchSalesOrderById response
            order.stockStatus = null;
          }
          const {
            eligible: isEligible,
            reason,
            code,
          } = determineEligibility(order);
          if (isEligible) {
            eligible.push(order);
          } else {
            ineligible.push({ order, reason, code });
          }
        } else {
          const reason =
            result.status === 'rejected'
              ? `Failed to fetch: ${getErrorMessage(result.reason)}`
              : 'Failed to fetch order details.';
          const placeholderOrder =
            createDefaultSalesOrderPlaceholder(originalId);
          ineligible.push({
            order: placeholderOrder,
            reason,
            code: BQF_INELIGIBILITY_CODES.NOT_EXISTING,
          });
        }
      });

      setBulkQuickFulfillEligibleOrders(eligible);
      setBulkQuickFulfillIneligibleOrders(ineligible);
      logInfoCtx('[BQF_Perf_Monitoring] Order preparation complete', {
        ...userContext,
        component: 'useBulkQuickFulfill',
        title: 'Bulk Quick Fulfill Preparation Results',
        description: `Eligible: ${eligible.length}, Ineligible: ${ineligible.length}`,
        eligibleOrderIds: eligible.map((o) =>
          o.id === null ? undefined : o.id
        ),
        ineligibleOrderCount: ineligible.length,
        ineligibleOrders: ineligible.map((item) => ({
          orderId: item.order.id === null ? undefined : item.order.id,
          reason: item.reason,
          code: item.code,
        })),
        bulkOperationId: currentBulkOperationId.current,
        preparationDurationMs: Date.now() - preparationStartTime,
      });

      if (eligible.length > 0 || ineligible.length > 0) {
        setIsBulkQuickFulfillModalOpen(true);
      }
    } catch (error) {
      showNotification(
        'Failed to prepare orders for bulk quick fulfillment. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setIsFetchingFullOrders(false);
    }
  }, [
    selectedOrderIds,
    fetchSalesOrderById,
    determineEligibility,
    stockStatusMap,
  ]);

  const closeBulkQuickFulfillModal = useCallback(() => {
    setIsBulkQuickFulfillModalOpen(false);
    setIsStoppingBulkQuickFulfill(false); // Reset stopping state
    // Stage and progress details will reset if openModalAndPrepareOrders is called again
  }, []);

  const stopBulkQuickFulfillProcessing = useCallback(() => {
    if (isStoppingRef.current) return;

    const fulfilledCountAtStop = bulkQuickFulfillProgressDetails.filter(
      (d) => d.status === 'fulfilled'
    ).length;
    const errorCountAtStop = bulkQuickFulfillProgressDetails.filter(
      (d) => d.status === 'error'
    ).length;
    const initialEligibleCount = bulkQuickFulfillEligibleOrders.length;
    const processedCount = fulfilledCountAtStop + errorCountAtStop;
    const pendingCountAtStop = initialEligibleCount - processedCount;

    const description = `User stopped BQF at stage: ${bulkQuickFulfillStage}. Progress: ${fulfilledCountAtStop} fulfilled, ${errorCountAtStop} errors, ${pendingCountAtStop} pending out of ${initialEligibleCount}.`;

    logInfoCtx('[BQF_Perf_Monitoring] stopBulkQuickFulfill called', {
      ...userContext,
      component: 'useBulkQuickFulfill',
      title: 'Bulk Quick Fulfill Stopped Manually',
      description,
      bulkOperationId: currentBulkOperationId.current,
      stoppedAtStage: bulkQuickFulfillStage,
      initialEligibleCount,
      fulfilledCountAtStop,
      errorCountAtStop,
      pendingCountAtStop,
    });

    isStoppingRef.current = true;
    setIsStoppingBulkQuickFulfill(true);
    // Mark any orders that are still pending or in the process of fulfilling as 'cancelled'
    setBulkQuickFulfillProgressDetails((prevDetails) =>
      prevDetails.map((detail) =>
        detail.status === 'pending' || detail.status === 'fulfilling'
          ? { ...detail, status: 'cancelled' }
          : detail
      )
    );
  }, [
    bulkQuickFulfillStage,
    bulkQuickFulfillProgressDetails,
    bulkQuickFulfillEligibleOrders.length,
  ]);

  const confirmAndProcessBulkQuickFulfill = useCallback(async () => {
    const estimatedProcessingDurationMs =
      bulkQuickFulfillEligibleOrders.length *
      ESTIMATED_TIME_PER_ORDER_SECONDS *
      1000;

    logInfoCtx(
      '[BQF_Perf_Monitoring] confirmAndProcessBulkQuickFulfill called',
      {
        ...userContext,
        component: 'useBulkQuickFulfill',
        title: 'Bulk Quick Fulfill Processing Started',
        description: `Processing ${
          bulkQuickFulfillEligibleOrders.length
        } eligible orders. Estimated time: ${
          estimatedProcessingDurationMs / 1000
        }s.`,
        eligibleOrderCount: bulkQuickFulfillEligibleOrders.length,
        eligibleOrderIds: bulkQuickFulfillEligibleOrders.map((o) =>
          o.id === null ? undefined : o.id
        ),
        bulkOperationId: currentBulkOperationId.current,
        estimatedProcessingDurationMs,
      }
    );
    setBulkQuickFulfillStage('progress');
    const initialProgressDetails: BulkQuickFulfillProgressDetail[] =
      bulkQuickFulfillEligibleOrders.map((order) => ({
        orderId: order.id!,
        number: order.number || String(order.id!),
        status: 'pending',
      }));
    setBulkQuickFulfillProgressDetails(initialProgressDetails);

    const processingStartTime = Date.now();
    let finalProgressDetails = [...initialProgressDetails];

    for (const order of bulkQuickFulfillEligibleOrders) {
      if (isStoppingRef.current) {
        // Mark remaining pending orders as cancelled
        finalProgressDetails = finalProgressDetails.map((detail) =>
          detail.status === 'pending'
            ? { ...detail, status: 'cancelled' }
            : detail
        );
        break;
      }

      // Update status to 'fulfilling' for the current order
      finalProgressDetails = finalProgressDetails.map((detail) =>
        detail.orderId === order.id
          ? { ...detail, status: 'fulfilling' }
          : detail
      );
      setBulkQuickFulfillProgressDetails(finalProgressDetails);

      const fulfillmentStartTime = Date.now();
      try {
        await quickFulfillSalesOrder(order.id!, { suppressErrorToast: true });
        const fulfillmentDurationMs = Date.now() - fulfillmentStartTime;
        finalProgressDetails = finalProgressDetails.map((detail) =>
          detail.orderId === order.id
            ? {
                ...detail,
                status: 'fulfilled',
                durationMs: fulfillmentDurationMs,
              }
            : detail
        );
      } catch (e) {
        const fulfillmentDurationMs = Date.now() - fulfillmentStartTime;
        finalProgressDetails = finalProgressDetails.map((detail) =>
          detail.orderId === order.id
            ? {
                ...detail,
                status: 'error',
                message: getErrorMessage(e) || 'An unknown error occurred',
                durationMs: fulfillmentDurationMs,
                errorCode: (e as any)?.code || 'UNKNOWN_FULFILLMENT_ERROR',
              }
            : detail
        );
      }
      setBulkQuickFulfillProgressDetails(finalProgressDetails);
    }

    const actualProcessingDurationMs = Date.now() - processingStartTime;
    const processingDurationDeltaMs =
      actualProcessingDurationMs - estimatedProcessingDurationMs;

    const fulfilledCount = finalProgressDetails.filter(
      (d) => d.status === 'fulfilled'
    ).length;
    const errorCount = finalProgressDetails.filter(
      (d) => d.status === 'error'
    ).length;
    const cancelledCount = finalProgressDetails.filter(
      (d) => d.status === 'cancelled'
    ).length;

    logInfoCtx(
      '[BQF_Perf_Monitoring] Bulk quick fulfilment processing finished',
      {
        ...userContext,
        component: 'useBulkQuickFulfill',
        title: 'Bulk Quick Fulfill Processing Summary',
        description: `Fulfilled: ${fulfilledCount}, Errors: ${errorCount}, Cancelled: ${cancelledCount}`,
        fulfilledCount,
        errorCount,
        cancelledCount,
        results: finalProgressDetails.map((detail) => ({
          orderId: detail.orderId,
          status: detail.status,
          message: detail.message,
          durationMs: detail.durationMs,
          errorCode: detail.errorCode,
        })),
        bulkOperationId: currentBulkOperationId.current,
        actualProcessingDurationMs,
      }
    );

    logInfoCtx('[BQF_Perf_Monitoring] Processing time comparison', {
      component: 'useBulkQuickFulfill',
      title: 'Bulk Quick Fulfill Timing Analysis',
      description: `Estimated: ${estimatedProcessingDurationMs}ms, Actual: ${actualProcessingDurationMs}ms, Delta: ${processingDurationDeltaMs}ms`,
      estimatedProcessingDurationMs,
      actualProcessingDurationMs,
      processingDurationDeltaMs,
      bulkOperationId: currentBulkOperationId.current,
      wasManuallyStopped: isStoppingRef.current,
      ...userContext,
    });

    setBulkQuickFulfillStage('summary');
  }, [
    bulkQuickFulfillEligibleOrders,
    quickFulfillSalesOrder,
    ESTIMATED_TIME_PER_ORDER_SECONDS,
  ]);

  // Effect for handling deselection and list refresh after summary
  useEffect(() => {
    if (bulkQuickFulfillStage === 'summary' && !hasRefreshedAfterBulkFulfill) {
      const successfullyFulfilledIds = bulkQuickFulfillProgressDetails
        .filter((detail) => detail.status === 'fulfilled')
        .map((detail) => detail.orderId);

      if (successfullyFulfilledIds.length > 0) {
        const currentTableSelectionIds = [...selectedOrderIds];
        const allSuccessfullyFulfilledWereInCurrentSelection =
          successfullyFulfilledIds.every((fulfilledId) =>
            currentTableSelectionIds.includes(fulfilledId)
          );
        const allCurrentSelectionWereSuccessfullyFulfilled =
          currentTableSelectionIds.every((selectedId) =>
            successfullyFulfilledIds.includes(selectedId)
          );

        if (
          allSuccessfullyFulfilledWereInCurrentSelection &&
          allCurrentSelectionWereSuccessfullyFulfilled &&
          currentTableSelectionIds.length === successfullyFulfilledIds.length
        ) {
          // Selection matches fulfilled, do nothing
        } else {
          const newSelectedItems = selectedOrderIds.filter(
            (selectedId) => !successfullyFulfilledIds.includes(selectedId)
          );
          setSelectedItems(newSelectedItems);
        }
      }

      if (fetchSearchResult) {
        fetchSearchResult();
        setHasRefreshedAfterBulkFulfill(true);
      }
    }
  }, [
    bulkQuickFulfillStage,
    bulkQuickFulfillProgressDetails,
    hasRefreshedAfterBulkFulfill,
    selectedOrderIds,
    setSelectedItems,
    fetchSearchResult,
  ]);

  // Effect to reset the refresh flag when the modal is no longer in summary stage
  useEffect(() => {
    if (bulkQuickFulfillStage !== 'summary') {
      if (hasRefreshedAfterBulkFulfill) {
        setHasRefreshedAfterBulkFulfill(false);
      }
    }
  }, [bulkQuickFulfillStage, hasRefreshedAfterBulkFulfill]);

  return {
    isBulkQuickFulfillModalOpen,
    bulkQuickFulfillStage,
    bulkQuickFulfillEligibleOrders,
    bulkQuickFulfillIneligibleOrders,
    bulkQuickFulfillProgressDetails,
    isFetchingFullOrders,
    isStoppingBulkQuickFulfill,
    openModalAndPrepareOrders,
    closeBulkQuickFulfillModal,
    confirmAndProcessBulkQuickFulfill,
    stopBulkQuickFulfillProcessing,
    estimatedTimePerOrderSeconds: ESTIMATED_TIME_PER_ORDER_SECONDS,
  };
};
